import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, LogOut, MapPin, Package, RotateCcw, Trash2, User, Download } from "lucide-react";
import {
  deleteAddress,
  downloadInvoice,
  formatApiError,
  getMe,
  getMyOrders,
  getProducts,
  inr,
  loginCustomer,
  registerCustomer,
  saveAddress,
} from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "@/components/ui/sonner";

const inputCls =
  "mt-1.5 w-full rounded-md border border-input bg-white/60 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary transition";

const STATUS_STYLES = {
  placed: "bg-secondary text-secondary-foreground",
  payment_pending: "bg-amber-100 text-amber-800",
  paid: "bg-emerald-100 text-emerald-800",
  confirmed: "bg-sky-100 text-sky-800",
  shipped: "bg-indigo-100 text-indigo-800",
  delivered: "bg-primary/15 text-primary",
  cancelled: "bg-red-100 text-red-700",
};

export default function AccountPage() {
  const { add, clear } = useCart();
  const [token, setToken] = useState(() => localStorage.getItem("classina_customer_token") || null);
  const [me, setMe] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [addr, setAddr] = useState({ label: "Home", address: "", city: "", state: "", pincode: "" });
  const [showAddrForm, setShowAddrForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (t) => {
    try {
      const [profile, myOrders, prods] = await Promise.all([getMe(t), getMyOrders(t), getProducts()]);
      setMe(profile);
      setOrders(myOrders);
      setProducts(prods);
      localStorage.setItem("classina_customer", JSON.stringify({ name: profile.name, email: profile.email, phone: profile.phone }));
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("classina_customer_token");
        setToken(null);
      }
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const submitAuth = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const data = mode === "login" ? await loginCustomer(form) : await registerCustomer(form);
      localStorage.setItem("classina_customer_token", data.token);
      setToken(data.token);
      toast.success(`Welcome${data.name ? `, ${data.name.split(" ")[0]}` : ""}!`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("classina_customer_token");
    localStorage.removeItem("classina_customer");
    setToken(null);
    setMe(null);
  };

  const addAddr = async (e) => {
    e.preventDefault();
    try {
      await saveAddress(token, addr);
      setShowAddrForm(false);
      setAddr({ label: "Home", address: "", city: "", state: "", pincode: "" });
      load(token);
      toast.success("Address saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const reorder = (order) => {
    let added = 0;
    let skipped = 0;
    clear();
    for (const item of order.items) {
      const product = products.find((p) => p.slug === item.slug);
      if (!product) {
        skipped++;
        continue;
      }
      const variant = item.variant ? product.variants?.find((v) => v.name === item.variant) || null : null;
      const pack = item.pack ? product.packs?.find((p) => p.label === item.pack) || null : null;
      add(product, item.qty, variant, pack);
      added++;
    }
    if (added) toast.success(`${added} item${added > 1 ? "s" : ""} added back to your cart`);
    if (skipped) toast.info(`${skipped} item${skipped > 1 ? "s" : ""} no longer available`);
  };

  if (!token || !me) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex justify-center mb-8" data-testid="account-logo">
            <img src="/classina-logo.png" alt="Classina Products" className="h-12 w-auto" />
          </Link>
          <form onSubmit={submitAuth} data-testid="account-auth-form" className="bg-white/70 backdrop-blur-sm border border-border/60 rounded-2xl p-8 shadow-sm">
            <h1 className="font-serif text-2xl font-semibold text-center">{mode === "login" ? "Welcome back" : "Create your account"}</h1>
            <p className="text-xs text-muted-foreground text-center mt-1 mb-6">
              {mode === "login" ? "Sign in to see orders, addresses and quick reorder." : "Faster checkout, saved addresses, order history."}
            </p>
            {mode === "register" && (
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Full name</span>
                <input data-testid="account-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Asha Patil" />
              </label>
            )}
            <label className="block mt-4">
              <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Email</span>
              <input data-testid="account-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="you@example.com" />
            </label>
            {mode === "register" && (
              <label className="block mt-4">
                <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Phone</span>
                <input data-testid="account-phone" required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} placeholder="+91 98765 43210" />
              </label>
            )}
            <label className="block mt-4">
              <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Password</span>
              <input data-testid="account-password" required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} placeholder="••••••••" />
            </label>
            {error && <p data-testid="account-error" className="text-sm text-destructive mt-3">{error}</p>}
            <button
              data-testid="account-submit-button"
              type="submit"
              disabled={busy}
              className="w-full mt-6 rounded-full bg-primary text-primary-foreground py-3 text-sm font-bold flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-60"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === "login" ? "Sign in" : "Create account"}
            </button>
            <button
              type="button"
              data-testid="account-toggle-mode"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="w-full text-center text-xs text-muted-foreground mt-4 hover:text-foreground transition-colors"
            >
              {mode === "login" ? "New to Classina? Create an account" : "Already have an account? Sign in"}
            </button>
          </form>
          <Link to="/" data-testid="account-back-link" className="block text-center text-xs text-muted-foreground mt-5 hover:text-foreground transition-colors">
            ← Continue shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="account-page">
      <header className="border-b border-border/50 bg-[#F5F4F0]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" data-testid="account-home-logo">
            <img src="/classina-logo.png" alt="Classina Products" className="h-9 w-auto" />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors">← Store</Link>
            <button
              data-testid="account-logout-button"
              onClick={logout}
              className="flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-xs font-bold hover:bg-secondary transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 grid lg:grid-cols-3 gap-8">
        <div className="space-y-6">
          <div className="bg-white/70 border border-border/60 rounded-2xl p-6">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                <User className="w-5 h-5" />
              </span>
              <div>
                <p data-testid="account-profile-name" className="font-serif text-lg font-semibold">{me.name}</p>
                <p className="text-xs text-muted-foreground">{me.email} · {me.phone}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/70 border border-border/60 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" /> Saved addresses
              </p>
              <button
                data-testid="account-add-address"
                onClick={() => setShowAddrForm(!showAddrForm)}
                className="text-xs font-bold text-primary hover:underline"
              >
                + Add
              </button>
            </div>
            {(me.addresses || []).length === 0 && !showAddrForm && (
              <p className="text-sm text-muted-foreground">No saved addresses yet.</p>
            )}
            <div className="space-y-3">
              {(me.addresses || []).map((a) => (
                <div key={a.id} data-testid={`address-${a.id}`} className="flex justify-between gap-3 text-sm border border-border/60 rounded-xl p-3.5">
                  <div>
                    <p className="font-bold text-xs uppercase tracking-wider">{a.label}</p>
                    <p className="text-muted-foreground mt-1">{a.address}, {a.city}, {a.state} — {a.pincode}</p>
                  </div>
                  <button
                    data-testid={`address-delete-${a.id}`}
                    onClick={async () => { await deleteAddress(token, a.id); load(token); }}
                    className="text-muted-foreground hover:text-destructive transition-colors h-fit"
                    aria-label="Delete address"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            {showAddrForm && (
              <form onSubmit={addAddr} className="mt-4 space-y-3" data-testid="address-form">
                <input required value={addr.address} onChange={(e) => setAddr({ ...addr, address: e.target.value })} placeholder="Flat, street, landmark" className={`${inputCls} mt-0`} data-testid="address-line-input" />
                <div className="grid grid-cols-3 gap-2">
                  <input required value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} placeholder="City" className={`${inputCls} mt-0`} data-testid="address-city-input" />
                  <input required value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} placeholder="State" className={`${inputCls} mt-0`} data-testid="address-state-input" />
                  <input required pattern="[0-9]{6}" maxLength={6} value={addr.pincode} onChange={(e) => setAddr({ ...addr, pincode: e.target.value })} placeholder="PIN" className={`${inputCls} mt-0`} data-testid="address-pincode-input" />
                </div>
                <button data-testid="address-save-button" className="rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-xs font-bold">
                  Save address
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="lg:col-span-2">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground mb-4 flex items-center gap-1.5">
            <Package className="w-3.5 h-3.5" /> Order history
          </p>
          {orders.length === 0 ? (
            <div className="bg-white/70 border border-border/60 rounded-2xl p-10 text-center">
              <p className="font-serif text-xl">No orders yet</p>
              <p className="text-sm text-muted-foreground mt-2">Your past orders (including guest orders with this email) will appear here.</p>
              <Link to="/" className="inline-block mt-5 rounded-full bg-primary text-primary-foreground px-8 py-3 text-sm font-semibold">
                Start shopping
              </Link>
            </div>
          ) : (
            <div className="space-y-4" data-testid="account-orders-list">
              {orders.map((o) => (
                <div key={o.id} data-testid={`account-order-${o.order_no}`} className="bg-white/70 border border-border/60 rounded-2xl p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-serif text-lg font-semibold">{o.order_no}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {new Date(o.created_at).toLocaleString("en-IN", { dateStyle: "medium" })} · {inr(o.total)}
                      </p>
                    </div>
                    <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-3 py-1.5 ${STATUS_STYLES[o.status] || "bg-secondary"}`}>
                      {o.status.replace("_", " ")}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-3">
                    {o.items.map((i) => `${i.name}${i.variant ? ` (${i.variant})` : ""}${i.pack ? ` [${i.pack}]` : ""} × ${i.qty}`).join(" · ")}
                  </p>
                  <div className="mt-4 flex gap-2">
                    <button
                      data-testid={`reorder-${o.order_no}`}
                      onClick={() => reorder(o)}
                      className="rounded-full bg-primary text-primary-foreground px-5 py-2 text-xs font-bold flex items-center gap-1.5 hover:-translate-y-0.5 transition-transform"
                    >
                      <RotateCcw className="w-3.5 h-3.5" /> Reorder
                    </button>
                    <button
                      data-testid={`invoice-${o.order_no}`}
                      onClick={() => downloadInvoice(o.order_no, me.phone)}
                      className="rounded-full border border-border px-5 py-2 text-xs font-bold flex items-center gap-1.5 hover:bg-secondary transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" /> Invoice
                    </button>
                    <Link
                      to="/track"
                      data-testid={`track-${o.order_no}`}
                      className="rounded-full border border-border px-5 py-2 text-xs font-bold hover:bg-secondary transition-colors"
                    >
                      Track
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
